import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Stack } from '@mui/material';
import { Button } from 'components/index';
import {
  DEFAULT_EXPORT_QUALITY,
  PDF_QUALITY_PRESETS,
  PdfImageQuality,
  PdfSizeInfo,
  estimatePresetSize,
  formatBytes,
} from '../utils/pdfCompression';

interface ExportQualityDialogProps {
  open: boolean;
  /** Size breakdown of the uncompressed preview; absent when the PDF service reported nothing. */
  baseline?: PdfSizeInfo;
  isExporting: boolean;
  onCancel: () => void;
  onConfirm: (quality: PdfImageQuality) => void;
}

const ExportQualityDialog: React.FC<ExportQualityDialogProps> = ({ open, baseline, isExporting, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState<PdfImageQuality>(DEFAULT_EXPORT_QUALITY);

  // The dialog stays mounted between opens (only `open` toggles), so without
  // this a prior pick would still be selected the next time it opens.
  useEffect(() => {
    if (open) setSelected(DEFAULT_EXPORT_QUALITY);
  }, [open]);

  const rows = useMemo(
    () =>
      PDF_QUALITY_PRESETS.map(preset => {
        const bytes = baseline ? estimatePresetSize(baseline, preset) : null;
        return {
          preset,
          bytes,
          // Only the uncompressed baseline is a measured figure; the rest are modelled.
          isEstimate: preset.maxWidth !== null,
        };
      }),
    [baseline],
  );

  // Label left, size right -- in the closed field and in the menu, so the
  // numbers line up in a column instead of running into the label.
  // No Typography variant here on purpose: the theme sizes MuiMenuItem at
  // size.sm, and forcing a variant would override it back to body1 (20px).
  const renderRow = (row: (typeof rows)[number]) => (
    <Stack direction='row' alignItems='center' sx={{ width: '100%', gap: 2 }}>
      <span>{row.preset.label}</span>
      <Box component='span' sx={{ ml: 'auto', color: 'text.secondary' }}>
        {row.bytes === null ? '' : `${row.isEstimate ? '≈ ' : ''}${formatBytes(row.bytes)}`}
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={isExporting ? undefined : onCancel}
      maxWidth='xs'
      fullWidth
      sx={{ '& .MuiDialog-paper': { maxWidth: 400 } }}
    >
      <DialogTitle>Select Resolution</DialogTitle>
      <DialogContent dividers sx={{ py: 2.5 }}>
        <Select
          fullWidth
          value={selected}
          disabled={isExporting}
          onChange={e => setSelected(e.target.value as PdfImageQuality)}
          renderValue={value => {
            const row = rows.find(r => r.preset.key === value);
            return row ? renderRow(row) : value;
          }}
          // The theme sizes menus at size.sm but leaves the closed input on
          // body1 (20px in this theme), so set it explicitly. size.md keeps it
          // on a theme token while giving the field some presence. A light
          // fill gives the control some weight instead of a bare outline
          // floating on the dialog's own white background.
          //
          // !important is required here: the theme's MuiOutlinedInput override
          // sets `padding: 12px` as one shorthand on this same node's
          // .MuiOutlinedInput-input class, which otherwise wins the cascade
          // against paddingTop/paddingBottom set through .MuiSelect-select --
          // plain py/px here is silently clamped back to 12px.
          sx={{
            backgroundColor: 'grey.100',
            borderRadius: ({ borders }: any) => borders.borderRadius.md,
            '& .MuiSelect-select': {
              fontSize: ({ typography }: any) => typography.size.md,
              paddingTop: '28px !important',
              paddingBottom: '28px !important',
              paddingLeft: '20px !important',
              paddingRight: '20px !important',
            },
          }}
        >
          {rows.map(row => (
            <MenuItem
              key={row.preset.key}
              value={row.preset.key}
              // Match the closed field, or the text jumps size/position on open.
              sx={{ py: 1.5, px: 2.5, fontSize: ({ typography }: any) => typography.size.md }}
            >
              {renderRow(row)}
            </MenuItem>
          ))}
        </Select>

        {!baseline ? (
          <Alert severity='info' sx={{ mt: 2, py: 0 }}>
            Sizes unavailable for this preview.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant='outlined' onClick={onCancel} disabled={isExporting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={() => onConfirm(selected)} loading={isExporting}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportQualityDialog;
